import { createTheme } from '@mui/material/styles';
import type {} from '@mui/x-data-grid/themeAugmentation';

// Modern Maritime Admin Panel Theme with sophisticated marine blue palette
export const adminTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#125594', // Marine Blue
      light: '#4A7BC8',
      dark: '#0F4375',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#7A8CA6', // Mid-grey
      light: '#CAD4DF',
      dark: '#5A6B7F',
      contrastText: '#ffffff',
    },
    background: {
      default: '#F7F9FC', // Very light gray
      paper: '#ffffff',
    },
    text: {
      primary: '#1E2A38', // Dark blue-grey
      secondary: '#7A8CA6', // Mid-grey
    },
    grey: {
      50: '#F7F9FC',
      100: '#E5EAF1',
      200: '#CAD4DF',
      300: '#A8B6C7',
      400: '#8395A7',
      500: '#7A8CA6',
      600: '#5A6B7F',
      700: '#4A5A6B',
      800: '#1E2A38',
      900: '#0C1B28',
    },
    error: {
      main: '#D32F2F',
      light: '#EF5350',
      dark: '#B00020',
    },
    warning: {
      main: '#D9822B',
      light: '#FFA726',
      dark: '#B8681F',
    },
    info: {
      main: '#00A3E0', // Light marine cyan
      light: '#33B5E6',
      dark: '#0082B3',
    },
    success: {
      main: '#0F8B4D',
      light: '#2E7D32',
      dark: '#0C6B3D',
    },
    // Custom colors for maritime admin panel
    action: {
      hover: '#F0F4FA',
      selected: '#E5EAF1',
      disabled: '#CAD4DF',
      disabledBackground: '#F7F9FC',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2.25rem',
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '-0.025em',
      color: '#0C1B28',
    },
    h2: {
      fontSize: '1.875rem',
      fontWeight: 600,
      lineHeight: 1.3,
      letterSpacing: '-0.025em',
      color: '#1E2A38',
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.3,
      color: '#1E2A38',
    },
    h4: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.4,
      color: '#1E2A38',
    },
    h5: {
      fontSize: '1.125rem',
      fontWeight: 600,
      lineHeight: 1.4,
      color: '#4A5A6B',
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.5,
      color: '#4A5A6B',
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: 1.5,
      color: '#5A6B7F',
    },
    subtitle2: {
      fontSize: '0.875rem',
      fontWeight: 500,
      lineHeight: 1.5,
      color: '#7A8CA6',
    },
    body1: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: 1.6,
      color: '#4A5A6B',
    },
    body2: {
      fontSize: '0.875rem',
      fontWeight: 400,
      lineHeight: 1.5,
      color: '#7A8CA6',
    },
    caption: {
      fontSize: '0.75rem',
      fontWeight: 400,
      lineHeight: 1.4,
      color: '#8395A7',
    },
    button: {
      fontSize: '0.875rem',
      fontWeight: 500,
      textTransform: 'none',
      letterSpacing: '0.025em',
    },
  },
  spacing: 8,
  shape: {
    borderRadius: 8,
  },
  shadows: [
    'none',
    '0 1px 2px 0 rgba(0, 0, 0, 0.05)', // 1
    '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)', // 2
    '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)', // 3
    '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)', // 4
    '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)', // 5
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)', // 6
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)', // 7
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)', // 8
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)', // 9
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)', // 10
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)', // 11
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)', // 12
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)', // 13
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)', // 14
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)', // 15
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)', // 16
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)', // 17
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)', // 18
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)', // 19
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)', // 20
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)', // 21
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)', // 22
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)', // 23
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)', // 24
  ],
  components: {
    // AppBar - Slim and flat
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          color: '#1E2A38',
          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
          borderBottom: '1px solid #CAD4DF',
          height: '64px',
          '& .MuiToolbar-root': {
            height: '64px',
            minHeight: '64px !important',
            paddingLeft: '24px',
            paddingRight: '24px',
          },
        },
      },
    },

    // Drawer - Sleek sidebar
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#F7F9FC',
          borderRight: '1px solid #CAD4DF',
          width: '280px',
          '& .MuiList-root': {
            padding: '16px 12px',
          },
        },
      },
    },

    // List Items - Navigation styling
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          margin: '2px 0',
          padding: '12px 16px',
          color: '#7A8CA6',
          '&:hover': {
            backgroundColor: '#F0F4FA',
            color: '#4A5A6B',
          },
          '&.Mui-selected': {
            backgroundColor: '#125594',
            color: '#ffffff',
            '&:hover': {
              backgroundColor: '#0F4375',
              color: '#ffffff',
            },
            '& .MuiListItemIcon-root': {
              color: '#ffffff',
            },
            '& .MuiListItemText-primary': {
              color: '#ffffff',
            },
            '& .MuiListItemText-secondary': {
              color: 'rgba(255, 255, 255, 0.7)',
            },
          },
        },
      },
    },

    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: '#7A8CA6',
          minWidth: '40px',
        },
      },
    },

    // Buttons - Modern and clean
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          textTransform: 'none',
          fontWeight: 500,
          padding: '10px 20px',
          fontSize: '0.875rem',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        contained: {
          backgroundColor: '#125594',
          color: '#ffffff',
          '&:hover': {
            backgroundColor: '#0F4375',
            color: '#ffffff',
          },
          '&:focus': {
            backgroundColor: '#0F4375',
            color: '#ffffff',
          },
          '&:disabled': {
            backgroundColor: '#CAD4DF',
            color: '#8395A7',
          },
        },
        outlined: {
          borderColor: '#CAD4DF',
          color: '#7A8CA6',
          '&:hover': {
            borderColor: '#8395A7',
            backgroundColor: '#F7F9FC',
          },
        },
        text: {
          color: '#7A8CA6',
          '&:hover': {
            backgroundColor: '#F0F4FA',
          },
        },
      },
    },

    // IconButton - Clean action buttons
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: '6px',
          padding: '8px',
          color: '#7A8CA6',
          '&:hover': {
            backgroundColor: '#F0F4FA',
            color: '#4A5A6B',
          },
          // Primary color for edit actions
          '&.MuiIconButton-colorPrimary': {
            color: '#125594',
            '&:hover': {
              backgroundColor: 'rgba(18, 85, 148, 0.08)',
              color: '#0F4375',
            },
          },
          // Info color for general actions
          '&.MuiIconButton-colorInfo': {
            color: '#00A3E0',
            '&:hover': {
              backgroundColor: 'rgba(0, 163, 224, 0.08)',
              color: '#0082B3',
            },
          },
          // Error color for delete actions
          '&.MuiIconButton-colorError': {
            color: '#D32F2F',
            '&:hover': {
              backgroundColor: 'rgba(211, 47, 47, 0.08)',
              color: '#B00020',
            },
          },
          // Warning color for warning actions
          '&.MuiIconButton-colorWarning': {
            color: '#D9822B',
            '&:hover': {
              backgroundColor: 'rgba(217, 130, 43, 0.08)',
              color: '#B8681F',
            },
          },
          // Success color for success actions
          '&.MuiIconButton-colorSuccess': {
            color: '#0F8B4D',
            '&:hover': {
              backgroundColor: 'rgba(15, 139, 77, 0.08)',
              color: '#0C6B3D',
            },
          },
        },
        sizeSmall: {
          padding: '6px',
        },
      },
    },

    // Cards - Elevated and rounded
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
          border: '1px solid #E5EAF1',
          '&:hover': {
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          },
        },
      },
    },

    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '24px',
          '&:last-child': {
            paddingBottom: '24px',
          },
        },
      },
    },

    // Paper - Consistent elevation
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
          border: '1px solid #E5EAF1',
        },
        elevation0: {
          boxShadow: 'none',
          border: 'none',
        },
        elevation1: {
          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
        },
      },
    },

    // Text Fields - Modern form inputs
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            backgroundColor: '#ffffff',
            '& fieldset': {
              borderColor: '#CAD4DF',
            },
            '&:hover fieldset': {
              borderColor: '#8395A7',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#125594',
              borderWidth: '2px',
            },
          },
          '& .MuiInputLabel-root': {
            color: '#7A8CA6',
            '&.Mui-focused': {
              color: '#125594',
            },
          },
        },
      },
    },

    // Dialogs - Clean modal styling
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: '12px',
          padding: '8px',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        },
      },
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: '1.25rem',
          fontWeight: 600,
          color: '#1E2A38',
          padding: '24px 24px 16px',
        },
      },
    },

    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '0 24px',
        },
      },
    },

    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '16px 24px 24px',
          gap: '12px',
        },
      },
    },

    // Chips - Status indicators
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '6px',
          fontWeight: 500,
          fontSize: '0.75rem',
        },
        outlined: {
          borderWidth: '1px',
        },
        colorSuccess: {
          backgroundColor: '#E8F4F0',
          color: '#0C6B3D',
          borderColor: '#B8E6D0',
        },
        colorError: {
          backgroundColor: '#FFEBEE',
          color: '#B00020',
          borderColor: '#FFCDD2',
        },
        colorWarning: {
          backgroundColor: '#FFF3E0',
          color: '#B8681F',
          borderColor: '#FFE0B2',
        },
        colorInfo: {
          backgroundColor: '#E3F2FD',
          color: '#0082B3',
          borderColor: '#BBDEFB',
        },
      },
    },

    // Snackbar - Notification styling
    MuiSnackbar: {
      styleOverrides: {
        root: {
          '& .MuiSnackbarContent-root': {
            borderRadius: '8px',
            backgroundColor: '#1E2A38',
            color: '#ffffff',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
          },
        },
      },
    },
    // DataGrid - Modern table styling with zebra stripes
    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: '1px solid #CAD4DF',
          borderRadius: '12px',
          overflow: 'hidden',
          backgroundColor: '#ffffff',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',

          // Header styling
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#E5EAF1',
            borderBottom: '1px solid #CAD4DF',
            color: '#5A6B7F',
            fontWeight: 600,
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            minHeight: '48px !important',
          },

          '& .MuiDataGrid-columnHeader': {
            outline: 'none',
            padding: '0 16px',
            display: 'flex',
            alignItems: 'center',
            '&:focus': {
              outline: 'none',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 600,
            },
          },

          // Cell styling
          '& .MuiDataGrid-cell': {
            border: 'none',
            fontSize: '0.875rem',
            color: '#4A5A6B',
            padding: '12px 16px',
            display: 'flex',
            alignItems: 'center',
            '&:focus': {
              outline: 'none',
            },
          },

          // Row styling with zebra stripes
          '& .MuiDataGrid-row': {
            '&:nth-of-type(even)': {
              backgroundColor: '#F7F9FC',
            },
            '&:hover': {
              backgroundColor: '#F0F4FA !important',
            },
            '&.Mui-selected': {
              backgroundColor: '#E3F2FD !important',
              '&:hover': {
                backgroundColor: '#BBDEFB !important',
              },
              '&:nth-of-type(even)': {
                backgroundColor: '#E3F2FD !important',
              },
            },
          },

          // Footer styling
          '& .MuiDataGrid-footerContainer': {
            borderTop: '1px solid #CAD4DF',
            backgroundColor: '#E5EAF1',
            color: '#7A8CA6',
            minHeight: '52px',
            fontSize: '0.875rem',
          },

          // Toolbar styling
          '& .MuiDataGrid-toolbarContainer': {
            padding: '16px 24px',
            borderBottom: '1px solid #CAD4DF',
            backgroundColor: '#ffffff',
            '& .MuiButton-root': {
              color: '#7A8CA6',
              fontSize: '0.875rem',
            },
          },

          // Selection styling
          '& .MuiDataGrid-selectedRowCount': {
            color: '#7A8CA6',
          },

          // Remove internal borders
          '& .MuiDataGrid-virtualScroller': {
            backgroundColor: '#ffffff',
          },

          // Loading overlay
          '& .MuiDataGrid-overlay': {
            backgroundColor: 'rgba(247, 249, 252, 0.8)',
          },

          // Pagination
          '& .MuiTablePagination-root': {
            color: '#7A8CA6',
          },

          '& .MuiTablePagination-select': {
            fontSize: '0.875rem',
          },

          '& .MuiTablePagination-displayedRows': {
            fontSize: '0.875rem',
          },
        },
      },
    },

    // Tooltip - Light tooltips with white text for shortcuts
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#333333',
          color: '#ffffff !important',
          fontSize: '0.75rem',
          padding: '8px 12px',
          '& *': {
            color: '#ffffff !important',
          },
          '& kbd': {
            color: '#ffffff !important',
            backgroundColor: 'transparent',
          },
        },
      },
    },
  },
});
